import {useEffect, useRef, useCallback} from 'react'
import {AuthModel} from './_models'
import * as authHelper from './AuthHelpers'

class TokenExpirationMiddleware {
  private intervalRef: NodeJS.Timeout | null = null
  private focusHandler: (() => void) | null = null
  private isMonitoring = false
  private onTokenExpired?: () => void
  private hasTriggeredExpiration = false // Prevent multiple triggers

  constructor(onTokenExpired?: () => void) {
    this.onTokenExpired = onTokenExpired
  }

  public setOnTokenExpired(callback?: () => void) {
    this.onTokenExpired = callback
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Math.floor(Date.now() / 1000)
      // Add 5 second buffer to avoid edge cases
      return payload.exp <= currentTime + 5
    } catch (error) {
      console.error('Error parsing token in middleware:', error)
      return true // If we can't parse the token, consider it expired
    }
  }

  private checkTokenExpiration(): boolean {
    const auth = authHelper.getAuth()

    if (!auth?.token) {
      this.stopMonitoring()
      return false
    }

    if (this.isTokenExpired(auth.token)) {
      console.log('Token expired detected by middleware')
      this.handleTokenExpiration()
      return true
    }

    return false
  }

  private handleTokenExpiration() {
    // Prevent multiple triggers
    if (this.hasTriggeredExpiration) {
      return
    }

    console.log('Token expired, triggering modal and cleaning up monitoring')
    this.hasTriggeredExpiration = true

    // Stop monitoring first to prevent loops
    this.stopMonitoring()

    // Call the callback if provided (for showing modal)
    if (this.onTokenExpired) {
      this.onTokenExpired()
    } else {
      // Fallback behavior - clean up and redirect
      this.performCleanupAndRedirect()
    }
  }

  // Separate method for cleanup and redirect (can be called by modal)
  public performCleanupAndRedirect() {
    console.log('Performing cleanup and redirect')

    // Clean up auth data
    authHelper.removeAuth()
    authHelper.removeUser()

    // Only redirect if not already on auth pages
    const currentPath = window.location.pathname
    if (!currentPath.includes('/auth/')) {
      window.location.href = '/auth/login'
    }
  }

  // Reset expiration flag when starting monitoring (for re-login scenarios)
  public resetExpirationFlag() {
    this.hasTriggeredExpiration = false
  }

  public startMonitoring(intervalMs: number = 30000) {
    if (this.isMonitoring) {
      return
    }

    const auth = authHelper.getAuth()
    if (!auth?.token) {
      console.log('No token found, not starting monitoring')
      return
    }

    // Reset expiration flag when starting new monitoring session
    this.resetExpirationFlag()

    // Check immediately
    if (this.checkTokenExpiration()) {
      return // Token already expired
    }

    console.log('Starting token expiration monitoring')
    this.isMonitoring = true

    // Set up periodic checking
    this.intervalRef = setInterval(() => {
      this.checkTokenExpiration()
    }, intervalMs)

    // Check on window focus
    this.focusHandler = () => {
      console.log('Window focused, checking token expiration...')
      this.checkTokenExpiration()
    }

    window.addEventListener('focus', this.focusHandler)
  }

  public stopMonitoring() {
    if (!this.isMonitoring) {
      return
    }

    console.log('Stopping token expiration monitoring')
    this.isMonitoring = false

    if (this.intervalRef) {
      clearInterval(this.intervalRef)
      this.intervalRef = null
    }

    // Remove focus handler if it exists
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler)
      this.focusHandler = null
    }
  }

  public isCurrentlyMonitoring(): boolean {
    return this.isMonitoring
  }

  public checkTokenNow(): boolean {
    return this.checkTokenExpiration()
  }
}

// React hook wrapper for the middleware
export const useTokenExpirationMiddleware = (
  auth: AuthModel | undefined,
  onTokenExpired?: () => void
) => {
  const middlewareRef = useRef<TokenExpirationMiddleware | null>(null)

  if (!middlewareRef.current) {
    middlewareRef.current = new TokenExpirationMiddleware(onTokenExpired)
  }

  useEffect(() => {
    if (middlewareRef.current) {
      middlewareRef.current.setOnTokenExpired(onTokenExpired)
    }
  }, [onTokenExpired])

  useEffect(() => {
    if (auth?.token) {
      middlewareRef.current?.startMonitoring()
    } else {
      middlewareRef.current?.stopMonitoring()
    }

    return () => {
      middlewareRef.current?.stopMonitoring()
    }
  }, [auth?.token])

  return middlewareRef.current
}

// Export the class for direct usage
export default TokenExpirationMiddleware
